import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { Article, Event } from '../types';
import { ArticleContent } from './ArticleContent';

type FeedItem = {
  type: 'article' | 'event';
  data: Article | Event;
  date: Date;
};

interface FeedItemProps {
  item: FeedItem;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const FeedItem: React.FC<FeedItemProps> = ({ item }) => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleShare = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy URL:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  if (item.type === 'article') {
    const article = item.data as Article;
    const articleUrl = `${window.location.origin}/news/${article.id}`;
    const shareId = `article-${article.id}`;
    return (
      <article className='bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 relative group'>
        {/* Icon buttons at top right */}
        <div className='absolute top-4 right-4 flex gap-2 z-10'>
          <button
            onClick={() => handleShare(articleUrl, shareId)}
            className='p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors'
            title={copiedId === shareId ? t('feed.link_copied') : t('feed.copy_link')}
          >
            {copiedId === shareId ? (
              <svg className='w-5 h-5 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
              </svg>
            ) : (
              <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
              </svg>
            )}
          </button>
          <Link
            to={`/news/${article.id}`}
            className='p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors'
            title={t('feed.view_article')}
          >
            <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
            </svg>
          </Link>
        </div>
        
        {/* Image at top */}
        {article.image_path && (
          <div className='w-full'>
            <img
              src={apiService.getArticleImageUrl(article.id)}
              alt={article.title}
              className='w-full h-auto max-w-full object-contain transition-transform'
              loading='lazy'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Content below image */}
        <div className='p-4 sm:p-8'>
          <div className='flex items-center gap-2 mb-4'>
            <span className='text-xs text-gray-500 uppercase tracking-wide font-medium font-sans'>
              {t('article.article')}
            </span>
            <span className='text-gray-300'>•</span>
            <time className='text-xs text-gray-500 font-sans'>
              {formatDate(article.published_at || article.created_at)}
            </time>
          </div>
          <h3 className='text-church-brown text-xl sm:text-2xl mb-4 font-semibold leading-tight font-sans'>
            {article.title}
          </h3>
          <div className='prose prose-base max-w-none text-gray-700 leading-relaxed font-sans mb-6'>
            <ArticleContent content={article.content} />
          </div>
          
          {/* Audio Player */}
          {(article.audio_files && article.audio_files.length > 0) || article.audio_path ? (
            <div className='mb-6 pt-4 border-t border-gray-200'>
              <h4 className='text-sm font-semibold text-gray-700 mb-3 font-sans flex items-center gap-2'>
                <svg className='w-5 h-5 text-church-brown' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3' />
                </svg>
                {t('article.audio')}
              </h4>
              <div className='space-y-3'>
                {article.audio_files && article.audio_files.length > 0 ? (
                  article.audio_files.map((audioFile, index) => (
                    <div key={index} className='bg-gray-50 rounded-lg p-4'>
                      <p className='text-sm font-medium text-gray-700 mb-2'>{audioFile.caption}</p>
                      <audio
                        controls
                        src={apiService.getArticleAudioUrl(article.id, index)}
                        className='w-full'
                        style={{ pointerEvents: 'auto' }}
                      >
                        {t('common.browser_no_audio')}
                      </audio>
                    </div>
                  ))
                ) : article.audio_path ? (
                  // Fallback for old single audio format
                  <div className='bg-gray-50 rounded-lg p-4'>
                    <audio
                      controls
                      src={apiService.getArticleAudioUrl(article.id)}
                      className='w-full'
                      style={{ pointerEvents: 'auto' }}
                    >
                      {t('common.browser_no_audio')}
                    </audio>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          
          {article.files && article.files.length > 0 && (
            <div className='mt-4 pt-4 border-t border-gray-200'>
              <h4 className='text-sm font-semibold text-gray-700 mb-2 font-sans'>{t('article.attached_files')}:</h4>
              <div className='flex flex-wrap gap-2'>
                {article.files.map((file, index) => {
                  // Extract filename and clean it up (remove UUID suffix if present)
                  let fileName = file.split('/').pop() || `${t('article.file')} ${index + 1}`;
                  // Remove UUID suffix pattern: _xxxxxxxx.ext -> .ext, then reconstruct
                  const match = fileName.match(/^(.+)_[a-f0-9]{8}(\.[^.]+)$/);
                  if (match) {
                    fileName = match[1] + match[2]; // Original name + extension
                  }
                  return (
                    <a
                      key={index}
                      href={apiService.getArticleFileUrl(article.id, index)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 px-3 py-1.5 text-sm text-church-brown bg-church-light rounded-md hover:bg-church-gold/20 transition-colors font-sans'
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                      </svg>
                      {fileName}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>
    );
  } else {
    const event = item.data as Event;
    const eventUrl = `${window.location.origin}/events/${event.id}`;
    const shareId = `event-${event.id}`;
    return (
      <article className='bg-gradient-to-br from-church-light to-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 relative group'>
        {/* Icon buttons at top right */}
        <div className='absolute top-4 right-4 flex gap-2 z-10'>
          <button
            onClick={() => handleShare(eventUrl, shareId)}
            className='p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors'
            title={copiedId === shareId ? t('feed.link_copied') : t('feed.copy_link')}
          >
            {copiedId === shareId ? (
              <svg className='w-5 h-5 text-green-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
              </svg>
            ) : (
              <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
              </svg>
            )}
          </button>
          <Link
            to={`/events/${event.id}`}
            className='p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors'
            title={t('event.view_event')}
          >
            <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
            </svg>
          </Link>
        </div>
        
        {/* Image at top */}
        {event.image_path && (
          <div className='w-full'>
            <img
              src={apiService.getEventImageUrl(event.id)}
              alt={event.title}
              className='w-full h-auto max-w-full object-contain'
              loading='lazy'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* Content below image */}
        <div className='p-4 sm:p-8'>
          <div className='flex items-center gap-2 mb-4'>
            <span className='text-xs text-church-gold uppercase tracking-wide font-semibold font-sans'>
              {t('event.event')}
            </span>
            <span className='text-gray-300'>•</span>
            <time className='text-xs text-gray-500 font-sans'>
              {formatDate(event.event_date)} à {formatTime(event.event_date)}
            </time>
          </div>
          <h3 className='text-church-brown text-xl sm:text-2xl mb-4 font-semibold leading-tight font-sans'>
            {event.title}
          </h3>
          {event.location && (
            <div className='flex items-center gap-2 text-sm text-gray-600 mb-6 font-sans'>
              <svg className='w-4 h-4 text-church-gold' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' />
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 11a3 3 0 11-6 0 3 3 0 016 0z' />
              </svg>
              <span>{event.location}</span>
            </div>
          )}
          {event.description && (
            <div className='prose prose-base max-w-none text-gray-700 leading-relaxed font-sans mb-6'>
              <ArticleContent content={event.description} />
            </div>
          )}
          {event.files && event.files.length > 0 && (
            <div className='mt-4 pt-4 border-t border-gray-200'>
              <h4 className='text-sm font-semibold text-gray-700 mb-2 font-sans'>{t('article.attached_files')}:</h4>
              <div className='flex flex-wrap gap-2'>
                {event.files.map((file, index) => {
                  const fileName = file.split('/').pop() || `${t('article.file')} ${index + 1}`;
                  return (
                    <a
                      key={index}
                      href={apiService.getEventFileUrl(event.id, index)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 px-3 py-1.5 text-sm text-church-brown bg-church-light rounded-md hover:bg-church-gold/20 transition-colors font-sans'
                    >
                      <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
                      </svg>
                      {fileName}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>
    );
  }
};
